import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, memo, useContext } from "react";
import { useStyles } from "./styles";
import { buildTextList, buildMatrix, percentColor } from "./utils";
import { AppContext } from "../../AppContext";

export default memo(function SimilarityHeatmap({ comparison, onCellHover, onCellClick, threshold, separateMirroredView = true }) {
    const [lo, hi] = Array.isArray(threshold) ? threshold : [0, threshold ?? 0];
    const classes = useStyles();
    const state = useContext(AppContext);
    const tc = (state.strings || {}).textComparisons || {};
    // Memoize so the event-listener effect doesn't re-register on every parent re-render
    const texts = useMemo(() => buildTextList(comparison.sources), [comparison.sources]);
    const matrix = useMemo(() => buildMatrix(texts, comparison.sources), [texts, comparison.sources]);
    const n = texts.length;
    // Keep callback refs so the event-listener effect can call the latest props
    // without needing them in its dependency array (avoids listener teardown/re-add
    // on every render caused by onCellHover/onCellClick identity changes).
    const onCellHoverRef = useRef(onCellHover);
    const onCellClickRef = useRef(onCellClick);
    useEffect(() => { onCellHoverRef.current = onCellHover; }, [onCellHover]);
    useEffect(() => { onCellClickRef.current = onCellClick; }, [onCellClick]);
    const tcRef = useRef(tc);
    useEffect(() => { tcRef.current = tc; }, [tc]); // eslint-disable-line react-hooks/exhaustive-deps

    // Refs so the event-listener closure always reads the latest texts/matrix
    // without the effect needing them in its dependency array.
    const textsRef = useRef(texts);
    const matrixRef = useRef(matrix);
    useEffect(() => { textsRef.current = texts; }, [texts]);
    useEffect(() => { matrixRef.current = matrix; }, [matrix]);

    const wrapperRef = useRef(null);
    const [layout, setLayout] = useState({ cellSize: 36, rowHeaderW: 8, colHeaderH: 100, rightLabelW: 94 });
    const layoutRef = useRef(layout);
    const [isResizing, setIsResizing] = useState(false);
    const debounceRef = useRef(null);

    const LEFT_PAD = 8; // small fixed left margin; col headers extend upper-right, not left
    const BASE_COL_HEADER_H = 100;
    const MAX_CELL = 60;
    const MAX_COL_HEADER_H = 150;   // caps top label space
    const MAX_RIGHT_LABEL_W = 250;  // caps right label space
    const SCROLL_PAD = 16; // heatmapScroll has padding: theme.spacing(1) on each side (8px * 2)

    const compute = (el) => {
        if (!el) return;
        const { width, height } = el.getBoundingClientRect();
        if (!width || !height) return;
        const availW = width - SCROLL_PAD;
        const availH = height - SCROLL_PAD;
        // colH serves as both the top margin (diagonal col labels) and the right
        // margin (row labels). The left side only needs a small fixed padding.
        const cellW = Math.floor((availW - LEFT_PAD - MAX_RIGHT_LABEL_W) / n);
        const cellH = Math.floor((availH - MAX_COL_HEADER_H) / n);
        const rawCell = Math.min(cellW, cellH);
        const cellSize = Math.max(8, Math.min(rawCell, MAX_CELL));
        // colH: top margin for diagonal column labels — constrained by HEIGHT only.
        const colH = Math.max(BASE_COL_HEADER_H, Math.min(
            MAX_COL_HEADER_H,
            availH - n * cellSize,
        ));
        // rightLabelW: remaining horizontal space, capped at MAX_RIGHT_LABEL_W.
        const rightLabelW = Math.min(MAX_RIGHT_LABEL_W, Math.max(BASE_COL_HEADER_H, availW - LEFT_PAD - n * cellSize));
        const next = { cellSize, rowHeaderW: LEFT_PAD, colHeaderH: colH, rightLabelW };
        const prev = layoutRef.current;
        if (next.cellSize !== prev.cellSize || next.colHeaderH !== prev.colHeaderH || next.rightLabelW !== prev.rightLabelW) {
            layoutRef.current = next;
            setLayout(next);
        }
    };

    useLayoutEffect(() => { compute(wrapperRef.current); }, [n, comparison.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            setIsResizing(true);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                compute(el);
                setIsResizing(false);
            }, 150);
        });
        ro.observe(el);
        return () => { ro.disconnect(); clearTimeout(debounceRef.current); };
    }, [comparison.id, n]);

    const svgRef = useRef(null);
    const tooltipRef = useRef(null);
    const lastHoverRef = useRef({ r: -1, c: -1, label: -1 });

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;

        function setHighlight(r, c, on) {
            if (r < 0) return;
            // highlight both (r,c) and its mirror (c,r)
            [[r, c], [c, r]].forEach(([hr, hc]) => {
                const cell = svg.querySelector(`g[data-r="${hr}"][data-c="${hc}"]`);
                if (cell) {
                    const rect = cell.querySelector("rect");
                    if (rect) {
                        const bx = parseFloat(rect.dataset.bx);
                        const by = parseFloat(rect.dataset.by);
                        const bw = parseFloat(rect.dataset.bw);
                        const bh = parseFloat(rect.dataset.bh);
                        if (on) {
                            rect.setAttribute("x", bx + 1);
                            rect.setAttribute("y", by + 1);
                            rect.setAttribute("width", bw - 2);
                            rect.setAttribute("height", bh - 2);
                            rect.setAttribute("stroke", "#1565c0");
                            rect.setAttribute("stroke-width", "2");
                        } else {
                            rect.setAttribute("x", bx);
                            rect.setAttribute("y", by);
                            rect.setAttribute("width", bw);
                            rect.setAttribute("height", bh);
                            rect.setAttribute("stroke", "#e0e0e0");
                            rect.setAttribute("stroke-width", "1");
                        }
                    }
                }
            });
            const rowLabel = svg.querySelector(`text[data-rl="${r}"]`);
            if (rowLabel) {
                rowLabel.setAttribute("fill", on ? "#1565c0" : "#333");
                rowLabel.setAttribute("font-weight", on ? "700" : "400");
            }
            const colLabel = svg.querySelector(`text[data-cl="${c}"]`);
            if (colLabel) {
                colLabel.setAttribute("fill", on ? "#1565c0" : "#333");
                colLabel.setAttribute("font-weight", on ? "700" : "400");
            }
        }

        function setLabelHighlight(idx, on) {
            const matrix = matrixRef.current;
            for (let j = 0; j < n; j++) {
                if (j === idx) continue;
                [[idx, j], [j, idx]].forEach(([hr, hc]) => {
                    const cell = svg.querySelector(`g[data-r="${hr}"][data-c="${hc}"]`);
                    if (cell) {
                        const rect = cell.querySelector("rect");
                        if (rect && rect.dataset.match === "1") {
                            const bx = parseFloat(rect.dataset.bx);
                            const by = parseFloat(rect.dataset.by);
                            const bw = parseFloat(rect.dataset.bw);
                            const bh = parseFloat(rect.dataset.bh);
                            if (on) {
                                rect.setAttribute("x", bx + 1);
                                rect.setAttribute("y", by + 1);
                                rect.setAttribute("width", bw - 2);
                                rect.setAttribute("height", bh - 2);
                                rect.setAttribute("stroke", "#1565c0");
                                rect.setAttribute("stroke-width", "2");
                            } else {
                                rect.setAttribute("x", bx);
                                rect.setAttribute("y", by);
                                rect.setAttribute("width", bw);
                                rect.setAttribute("height", bh);
                                rect.setAttribute("stroke", "#e0e0e0");
                                rect.setAttribute("stroke-width", "1");
                            }
                        }
                    }
                });
            }
            const rowLabel = svg.querySelector(`text[data-rl="${idx}"]`);
            if (rowLabel) {
                rowLabel.setAttribute("fill", on ? "#1565c0" : "#333");
                rowLabel.setAttribute("font-weight", on ? "700" : "400");
            }
            const colLabel2 = svg.querySelector(`text[data-cl="${idx}"]`);
            if (colLabel2) {
                colLabel2.setAttribute("fill", on ? "#1565c0" : "#333");
                colLabel2.setAttribute("font-weight", on ? "700" : "400");
            }
            for (let j = 0; j < n; j++) {
                if (j === idx || matrix[idx][j] === null) continue;
                const rl = svg.querySelector(`text[data-rl="${j}"]`);
                if (rl) {
                    rl.setAttribute("fill", on ? "#1565c0" : "#333");
                    rl.setAttribute("font-weight", on ? "700" : "400");
                }
                const cl = svg.querySelector(`text[data-cl="${j}"]`);
                if (cl) {
                    cl.setAttribute("fill", on ? "#1565c0" : "#333");
                    cl.setAttribute("font-weight", on ? "700" : "400");
                }
            }
        }

        function moveTooltip(e) {
            const tip = tooltipRef.current;
            if (!tip) return;
            const offset = 14;
            const tw = tip.offsetWidth;
            const th = tip.offsetHeight;
            let x = e.clientX + offset;
            let y = e.clientY + offset;
            if (x + tw > window.innerWidth - 8) x = e.clientX - tw - offset;
            if (y + th > window.innerHeight - 8) y = e.clientY - th - offset;
            tip.style.left = `${x}px`;
            tip.style.top = `${y}px`;
        }

        function showTooltip(e, r, c) {
            const tip = tooltipRef.current;
            if (!tip) return;
            const texts = textsRef.current;
            const matrix = matrixRef.current;
            const cell = matrix[r][c];
            const p1 = cell ? cell.p1 : null;
            const p2 = cell ? cell.p2 : null;
            const mw = cell ? cell.mw : null;
            const fmtLine = (t, pct) => {
                const matchStr = mw != null && t.words != null ? `${mw}/${t.words} ${tcRef.current.words || "Words"}` : null;
                const pctStr = pct != null ? `(${pct.toFixed(1)}%)` : null;
                const stat = [matchStr, pctStr].filter(Boolean).join(" ");
                return stat ? `${t.name}\n${stat}` : t.name;
            };
            tip.textContent = `${fmtLine(texts[c], p2)}\n↕\n${fmtLine(texts[r], p1)}`;
            tip.style.display = "block";
            moveTooltip(e);
        }

        function hideTooltip() {
            const tip = tooltipRef.current;
            if (tip) tip.style.display = "none";
        }

        function showLabelTooltip(e, idx) {
            const tip = tooltipRef.current;
            if (!tip) return;
            const texts = textsRef.current;
            const matrix = matrixRef.current;
            const name = texts[idx] ? texts[idx].name : "";
            let maxSim = null;
            let count = 0;
            for (let j = 0; j < n; j++) {
                if (j === idx) continue;
                const val = matrix[idx][j];
                if (val !== null) {
                    count++;
                    if (maxSim === null || val.p1 > maxSim) maxSim = val.p1;
                }
            }
            const maxStr = maxSim !== null ? `${maxSim.toFixed(1)}%` : "n/a";
            tip.textContent = `${name}\n${tcRef.current.maxSimilarity || "Max. Similarity"}: ${maxStr}\n${tcRef.current.relatedTexts || "Number of related texts"}: ${count}`;
            tip.style.display = "block";
            moveTooltip(e);
        }

        function handleMouseOver(e) {
            const cellG = e.target.closest("g[data-r]");
            const rlElem = !cellG ? e.target.closest("text[data-rl]") : null;
            const clElem = !cellG && !rlElem ? e.target.closest("text[data-cl]") : null;
            const last = lastHoverRef.current;
            const newR = cellG ? parseInt(cellG.dataset.r, 10) : -1;
            const newC = cellG ? parseInt(cellG.dataset.c, 10) : -1;
            const newLabel = rlElem ? parseInt(rlElem.dataset.rl, 10)
                : clElem ? parseInt(clElem.dataset.cl, 10) : -1;
            if (newR === last.r && newC === last.c && newLabel === last.label) return;
            if (last.label >= 0) setLabelHighlight(last.label, false);
            else setHighlight(last.r, last.c, false);
            if (newLabel >= 0) {
                setLabelHighlight(newLabel, true);
                lastHoverRef.current = { r: -1, c: -1, label: newLabel };
                showLabelTooltip(e, newLabel);
                if (onCellHoverRef.current) onCellHoverRef.current(null);
            } else {
                setHighlight(newR, newC, true);
                lastHoverRef.current = { r: newR, c: newC, label: -1 };
                if (newR >= 0) {
                    showTooltip(e, newR, newC);
                    const texts = textsRef.current;
                    const matrix = matrixRef.current;
                    const hCell = matrix[newR][newC];
                    if (onCellHoverRef.current) onCellHoverRef.current({ r: newR, c: newC, val: hCell ? hCell.p1 : null, val2: hCell ? hCell.p2 : null, mw: hCell ? hCell.mw : null, rowName: texts[newR].name, colName: texts[newC].name, rowWords: texts[newR].words, colWords: texts[newC].words });
                } else {
                    hideTooltip();
                    if (onCellHoverRef.current) onCellHoverRef.current(null);
                }
            }
        }

        function handleMouseMove(e) {
            if (lastHoverRef.current.r >= 0 || lastHoverRef.current.label >= 0) moveTooltip(e);
        }

        function handleMouseLeave() {
            const last = lastHoverRef.current;
            if (last.label >= 0) setLabelHighlight(last.label, false);
            else setHighlight(last.r, last.c, false);
            lastHoverRef.current = { r: -1, c: -1, label: -1 };
            hideTooltip();
            if (onCellHoverRef.current) onCellHoverRef.current(null);
        }

        function handleClick(e) {
            const cellG = e.target.closest("g[data-r]");
            if (!cellG) return;
            const r = parseInt(cellG.dataset.r, 10);
            const c = parseInt(cellG.dataset.c, 10);
            const matrix = matrixRef.current;
            if (matrix[r][c] === null) return; // no-match cell (including zero-match padding)
            if (onCellClickRef.current) onCellClickRef.current({ r, c });
        }

        svg.addEventListener("mouseover", handleMouseOver);
        svg.addEventListener("mousemove", handleMouseMove);
        svg.addEventListener("mouseleave", handleMouseLeave);
        svg.addEventListener("click", handleClick);
        return () => {
            svg.removeEventListener("mouseover", handleMouseOver);
            svg.removeEventListener("mousemove", handleMouseMove);
            svg.removeEventListener("mouseleave", handleMouseLeave);
            svg.removeEventListener("click", handleClick);
        };
    }, [comparison.id, n]); // eslint-disable-line react-hooks/exhaustive-deps

    const { cellSize, rowHeaderW: ROW_HEADER_W, colHeaderH: COL_HEADER_H, rightLabelW: RIGHT_LABEL_W } = layout;
    // svgW always fills full available width; svgH may be less when width is binding.
    const svgW = ROW_HEADER_W + n * cellSize + RIGHT_LABEL_W;
    const svgH = COL_HEADER_H + n * cellSize;
    const fontSize = Math.max(11, Math.min(13, Math.round(cellSize * 0.3)));
    const showLabel = cellSize >= 22;
    const shortName = (name, maxChars) =>
        name.length > maxChars + 1 ? name.slice(0, maxChars) + "…" : name;
    // Diagonal text starts at y=COL_HEADER_H-10, going upper-right at -45°.
    // Clipped by container padding (8px above SVG top), so effective vertical = COL_HEADER_H-10-8 = COL_HEADER_H-18.
    const maxColChars = Math.max(6, Math.floor(((COL_HEADER_H - 18) * 1.41) / (fontSize * 0.6)));
    // Row labels: RIGHT_LABEL_W - 6px offset + 8px right padding the overflow:visible text can reach.
    const maxRowChars = Math.max(4, Math.floor((RIGHT_LABEL_W + 2) / (fontSize * 0.6)));

    return (
        <>
            <div ref={tooltipRef} className={classes.heatmapTooltip} />
            <div ref={wrapperRef} className={classes.heatmapWrapper}>
                <div className={classes.heatmapScroll}>
                    <svg ref={svgRef} width={svgW} height={svgH} style={{ display: "block", fontFamily: "inherit", overflow: "visible" }}>
                        <defs>
                            <linearGradient id="cellShading" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="white" stopOpacity="0.22" />
                                <stop offset="100%" stopColor="black" stopOpacity="0.14" />
                            </linearGradient>
                        </defs>
                        {/* Column headers */}
                        {!isResizing && texts.map((t, c) => {
                            const px = ROW_HEADER_W + c * cellSize + cellSize / 2;
                            const py = COL_HEADER_H - 10;
                            return (
                                <g key={t.id} style={{ cursor: "default" }}>
                                    <text
                                        x={px} y={py}
                                        transform={`rotate(-45,${px},${py})`}
                                        textAnchor="start"
                                        dominantBaseline="auto"
                                        fontSize={fontSize}
                                        fontWeight={400}
                                        fill="#333"
                                        data-cl={c}
                                    >
                                        {shortName(t.name, maxColChars)}
                                    </text>
                                </g>
                            );
                        })}

                        {/* Row headers + cells */}
                        {texts.map((rowText, r) => {
                            const cy = COL_HEADER_H + r * cellSize + cellSize / 2;
                            return (
                                <g key={rowText.id}>
                                    {/* Row header */}
                                    {!isResizing && (
                                        <g style={{ cursor: "default" }}>
                                            <text
                                                x={ROW_HEADER_W + n * cellSize + 6} y={cy}
                                                textAnchor="start"
                                                dominantBaseline="middle"
                                                fontSize={fontSize}
                                                fontWeight={400}
                                                fill="#333"
                                                data-rl={r}
                                            >
                                                {shortName(rowText.name, maxRowChars)}
                                            </text>
                                        </g>
                                    )}

                                    {/* Cells */}
                                    {texts.map((colText, c) => {
                                        const rx = ROW_HEADER_W + c * cellSize;
                                        const ry = COL_HEADER_H + r * cellSize;

                                        // In non-mirrored mode hide lower-triangle cells
                                        if (!separateMirroredView && r > c) {
                                            return (
                                                <g key={colText.id}>
                                                    <rect x={rx} y={ry} width={cellSize} height={cellSize} fill="#fafafa" stroke="#e0e0e0" strokeWidth={1} />
                                                </g>
                                            );
                                        }

                                        if (r === c) {
                                            return (
                                                <g key={colText.id}>
                                                    <rect
                                                        x={rx} y={ry}
                                                        width={cellSize} height={cellSize}
                                                        fill="#cccccc"
                                                        stroke="#e0e0e0" strokeWidth={1}
                                                    />
                                                    <rect x={rx} y={ry} width={cellSize} height={cellSize} fill="url(#cellShading)" style={{ pointerEvents: "none" }} />
                                                </g>
                                            );
                                        }

                                        const cell = matrix[r][c];
                                        const rawP1 = cell ? cell.p1 : null;
                                        const rawP2 = cell ? cell.p2 : null;
                                        // Mirrored ON: both triangles show column's % (p2 = top label's %).
                                        // Mirrored OFF: lower triangle is already hidden; show p1 as threshold basis.
                                        const displayRaw = separateMirroredView ? rawP2 : rawP1;
                                        const val = displayRaw !== null && (displayRaw < lo || displayRaw > hi) ? null : displayRaw;
                                        const bg = percentColor(val);

                                        if (isResizing) {
                                            return (
                                                <g key={colText.id}>
                                                    <rect
                                                        x={rx} y={ry}
                                                        width={cellSize} height={cellSize}
                                                        fill={bg}
                                                        stroke="#e0e0e0" strokeWidth={1}
                                                    />
                                                    <rect x={rx} y={ry} width={cellSize} height={cellSize} fill="url(#cellShading)" style={{ pointerEvents: "none" }} />
                                                </g>
                                            );
                                        }

                                        const textColor = val !== null && val > 40 ? "#fff" : "#333";
                                        return (
                                            <g
                                                key={colText.id}
                                                style={{ cursor: val !== null ? "pointer" : "default" }}
                                                data-r={r}
                                                data-c={c}
                                            >
                                                <rect
                                                    x={rx} y={ry}
                                                    width={cellSize} height={cellSize}
                                                    fill={bg}
                                                    stroke="#e0e0e0" strokeWidth={1}
                                                    data-bx={rx} data-by={ry}
                                                    data-bw={cellSize} data-bh={cellSize}
                                                    data-match={val !== null ? "1" : "0"}
                                                />
                                                <rect x={rx} y={ry} width={cellSize} height={cellSize} fill="url(#cellShading)" style={{ pointerEvents: "none" }} />
                                                {showLabel && val !== null && (
                                                    separateMirroredView ? (
                                                        // Mirrored view: single centered label (top label's %)
                                                        <text
                                                            x={rx + cellSize / 2}
                                                            y={ry + cellSize / 2}
                                                            textAnchor="middle"
                                                            dominantBaseline="middle"
                                                            fontSize={fontSize}
                                                            fontWeight={500}
                                                            fill={textColor}
                                                            style={{ pointerEvents: "none" }}
                                                        >
                                                            {`${val.toFixed(1)}%`}
                                                        </text>
                                                    ) : (
                                                        // Non-mirrored: two labels (lower triangle is hidden anyway)
                                                        <>
                                                            <text
                                                                x={rx + cellSize / 2}
                                                                y={ry + cellSize * 0.36}
                                                                textAnchor="middle"
                                                                dominantBaseline="middle"
                                                                fontSize={fontSize}
                                                                fontWeight={500}
                                                                fill={textColor}
                                                                style={{ pointerEvents: "none" }}
                                                            >
                                                                {rawP2 != null ? `${rawP2.toFixed(1)}%` : ""}
                                                            </text>
                                                            <text
                                                                x={rx + cellSize / 2}
                                                                y={ry + cellSize * 0.64}
                                                                textAnchor="middle"
                                                                dominantBaseline="middle"
                                                                fontSize={fontSize}
                                                                fontWeight={500}
                                                                fill={textColor}
                                                                style={{ pointerEvents: "none" }}
                                                            >
                                                                {`${val.toFixed(1)}%`}
                                                            </text>
                                                        </>
                                                    )
                                                )}
                                            </g>
                                        );
                                    })}
                                </g>
                            );
                        })}
                    </svg>
                </div>
            </div>
        </>
    );
});
